import { useMutation, useQuery } from "@tanstack/react-query";
import type {
  CreateFoodicsBranchMappingBody,
  FoodicsBranchMapping,
  FoodicsBranchMappingsResponse,
  FoodicsBranchesResponse,
  FoodicsIntegrationStatus,
  FoodicsInventoryItemEntity,
  FoodicsMenuEntitiesResponse,
  FoodicsMenuProductEntity,
  FoodicsModifierOptionEntity,
  FoodicsOrdersPreviewResponse,
  FoodicsSuppliersResponse,
  ImportFoodicsMenuEntitiesBody,
  ImportFoodicsOrdersBody,
  ImportFoodicsOrdersResponse,
  ImportFoodicsSuppliersBody,
  ImportFoodicsSuppliersResponse,
} from "src/types/foodics";
import { isProductionItem } from "src/types/foodics";
import { APIErrorResponse } from "src/types/generic";
import apiClient from "../apiClient";

// API Functions //

/**
 * apiClient resolves to the response body, which the API's ApiResponseTrait
 * still wraps as { data, errors }. Every type below describes the payload
 * inside that wrapper, so each call has to peel it off.
 */
const unwrap = async <T>(request: Promise<unknown>): Promise<T> =>
  ((await request) as { data: T }).data;

const getFoodicsStatus = async (): Promise<FoodicsIntegrationStatus> => {
  return unwrap<FoodicsIntegrationStatus>(apiClient.get("/api/foodics/status"));
};

const disconnectFoodics = async (): Promise<{ message: string }> => {
  return unwrap<{ message: string }>(apiClient.post("/api/foodics/disconnect"));
};

const getFoodicsBranches = async (): Promise<FoodicsBranchesResponse> => {
  return unwrap<FoodicsBranchesResponse>(apiClient.get("/api/foodics/branches"));
};

const getFoodicsBranchMappings =
  async (): Promise<FoodicsBranchMappingsResponse> => {
    return unwrap<FoodicsBranchMappingsResponse>(
      apiClient.get("/api/foodics/mappings/branches")
    );
  };

const createFoodicsBranchMapping = async (
  data: CreateFoodicsBranchMappingBody
): Promise<{ message: string; data: FoodicsBranchMapping }> => {
  return unwrap<{ message: string; data: FoodicsBranchMapping }>(
    apiClient.post("/api/foodics/mappings/branches", data)
  );
};

const deleteFoodicsBranchMapping = async (
  id: number
): Promise<{ message: string }> => {
  return unwrap<{ message: string }>(
    apiClient.delete(`/api/foodics/mappings/branches/${id}`)
  );
};

const getFoodicsMenuEntities = async (params?: {
  products_page?: number;
  products_per_page?: number;
  categories_page?: number;
  categories_per_page?: number;
  include_inventory?: boolean | 0 | 1;
  include_product_ingredients?: boolean | 0 | 1;
  include_modifier_options?: boolean | 0 | 1;
  modifier_options_page?: number;
  modifier_options_per_page?: number;
  inventory_page?: number;
  inventory_per_page?: number;
}): Promise<FoodicsMenuEntitiesResponse> => {
  return unwrap<FoodicsMenuEntitiesResponse>(
    apiClient.get("/api/foodics/menu/entities", { params })
  );
};

const importFoodicsMenuEntities = async (
  data: ImportFoodicsMenuEntitiesBody
): Promise<{
  message: string;
  data: {
    imported_categories: number;
    imported_products: number;
    imported_modifier_options?: number;
    imported_inventory_items?: number;
    skipped_inventory_items?: number;
    synced_production_recipes?: number;
    menu_recipes_synced?: number;
  };
}> => {
  return unwrap(apiClient.post("/api/foodics/menu/import", data));
};

const getFoodicsOrdersPreview = async (params?: {
  branch_id?: string;
  start_date?: string;
  end_date?: string;
  page?: number;
  per_page?: number;
}): Promise<FoodicsOrdersPreviewResponse> => {
  return unwrap<FoodicsOrdersPreviewResponse>(
    apiClient.get("/api/foodics/orders", { params })
  );
};

const importFoodicsOrders = async (
  data: ImportFoodicsOrdersBody
): Promise<ImportFoodicsOrdersResponse> => {
  return unwrap<ImportFoodicsOrdersResponse>(
    apiClient.post("/api/foodics/orders/import", data)
  );
};

const importFoodicsSuppliers = async (
  data?: ImportFoodicsSuppliersBody
): Promise<ImportFoodicsSuppliersResponse> => {
  return unwrap<ImportFoodicsSuppliersResponse>(
    apiClient.post("/api/foodics/suppliers/import", data ?? {})
  );
};

const getFoodicsSuppliers = async (params?: {
  page?: number;
  per_page?: number;
}): Promise<FoodicsSuppliersResponse> => {
  return unwrap<FoodicsSuppliersResponse>(
    apiClient.get("/api/foodics/suppliers", { params })
  );
};

type FoodicsInventoryImportPayloadItem = NonNullable<
  ImportFoodicsMenuEntitiesBody["inventory_items"]
>[number];

/**
 * Shared mapper: Foodics inventory entity → menu/import inventory_items payload row.
 * Pass includeIngredients: false to import a production item without its recipe.
 */
export const mapFoodicsInventoryItemToImportPayload = (
  row: FoodicsInventoryItemEntity,
  options?: { includeIngredients?: boolean }
): FoodicsInventoryImportPayloadItem => ({
  id: row.external_id as string,
  sku: row.sku,
  is_product: isProductionItem(row),
  cost: row.cost ?? undefined,
  name: row.name,
  ingredient_unit:
    typeof row.ingredient_unit === "string" ? row.ingredient_unit : undefined,
  storage_unit:
    typeof row.storage_unit === "string" ? row.storage_unit : undefined,
  ...(row.storage_to_ingredient_factor != null
    ? { storage_to_ingredient_factor: row.storage_to_ingredient_factor }
    : {}),
  ingredients:
    options?.includeIngredients === false
      ? []
      : Array.isArray(row.ingredients)
      ? row.ingredients
      : [],
  ...(row.minimum_level != null && row.minimum_level !== ""
    ? { minimum_level: row.minimum_level }
    : {}),
  ...(row.maximum_level != null && row.maximum_level !== ""
    ? { maximum_level: row.maximum_level }
    : {}),
  ...(row.supplier?.id
    ? { supplier: { id: row.supplier.id, name: row.supplier.name } }
    : {}),
  ...(row.suppliers?.length
    ? { suppliers: row.suppliers.map((s) => ({ id: s.id, name: s.name })) }
    : {}),
  ...(row.supplier_id ? { supplier_id: row.supplier_id } : {}),
});

const fetchAllFoodicsInventoryItems = async (): Promise<
  FoodicsInventoryItemEntity[]
> => {
  const all: FoodicsInventoryItemEntity[] = [];
  let page = 1;
  // Hard cap so a broken pagination response can never loop forever.
  for (let i = 0; i < 50; i++) {
    const response = await getFoodicsMenuEntities({
      include_inventory: 1,
      inventory_page: page,
      inventory_per_page: 200,
      products_per_page: 1,
      categories_per_page: 1,
    });
    const items = response.data?.inventory_items ?? [];
    all.push(...items);
    const pagination = response.data?.inventory_pagination;
    const lastPage = pagination?.last_page ?? 1;
    if (!pagination || items.length === 0 || page >= lastPage) {
      break;
    }
    page++;
  }
  return all;
};

/**
 * Workspace "Import inventory" direct action: imports every Foodics supplier,
 * then every not-yet-imported non-production inventory item (all pages).
 * Production items (has ingredients) are handled by the Production items step.
 */
const importAllFoodicsInventory = async (): Promise<{
  imported_items: number;
}> => {
  await importFoodicsSuppliers();
  const items = await fetchAllFoodicsInventoryItems();
  const payload = items
    .filter((row) => row.external_id && !row.already_imported && !isProductionItem(row))
    .map((row) => mapFoodicsInventoryItemToImportPayload(row));
  if (payload.length > 0) {
    await importFoodicsMenuEntities({
      categories: [],
      products: [],
      inventory_items: payload,
    });
  }
  return { imported_items: payload.length };
};

/**
 * Workspace "Import production" direct action: imports every not-yet-imported
 * Foodics production item (has ingredients), with or without its recipe.
 */
const importAllFoodicsProduction = async (options?: {
  withRecipe?: boolean;
}): Promise<{
  imported_items: number;
}> => {
  const withRecipe = options?.withRecipe ?? true;
  const items = await fetchAllFoodicsInventoryItems();
  const payload = items
    .filter((row) => row.external_id && !row.already_imported && isProductionItem(row))
    .map((row) =>
      mapFoodicsInventoryItemToImportPayload(row, {
        includeIngredients: withRecipe,
      })
    );
  if (payload.length > 0) {
    await importFoodicsMenuEntities({
      categories: [],
      products: [],
      inventory_items: payload,
    });
  }
  return { imported_items: payload.length };
};

type FoodicsProductImportPayloadItem = NonNullable<
  ImportFoodicsMenuEntitiesBody["products"]
>[number];

export const mapFoodicsProductToImportPayload = (
  p: FoodicsMenuProductEntity
): FoodicsProductImportPayloadItem => ({
  id: p.external_id,
  name: p.name,
  sku: p.sku?.trim() || undefined,
  price: p.price ?? null,
  ...(p.cost != null && String(p.cost).trim() !== ""
    ? { cost: Number(p.cost) }
    : {}),
  type: p.type ?? null,
  category: p.category
    ? {
        id: p.category.id,
        title: p.category.title,
        ...(p.category.sku?.trim() ? { sku: p.category.sku.trim() } : {}),
      }
    : null,
});

type FoodicsModifierImportPayloadItem = NonNullable<
  ImportFoodicsMenuEntitiesBody["modifier_options"]
>[number];

export const mapFoodicsModifierToImportPayload = (
  m: FoodicsModifierOptionEntity & { external_id: string }
): FoodicsModifierImportPayloadItem => ({
  id: m.external_id,
  name: m.name,
  sku: m.sku?.trim() || undefined,
  price: m.price ?? null,
});

/**
 * Workspace "Import menu" direct action: imports all categories, products,
 * and modifier options in one call.
 */
const importAllFoodicsMenu = async (options?: {
  withRecipe?: boolean;
}): Promise<{
  imported_categories: number;
  imported_products: number;
  imported_modifiers: number;
}> => {
  const data = await getFoodicsMenuEntities({
    categories_page: 1,
    categories_per_page: 500,
    products_page: 1,
    products_per_page: 500,
    include_modifier_options: 1,
    modifier_options_page: 1,
    modifier_options_per_page: 500,
  });

  const categories = data.data?.categories ?? [];
  const products = data.data?.products ?? [];
  const modifiers = data.data?.modifier_options ?? [];

  const catPayload = categories
    .filter((c) => !c.already_imported)
    .map((c) => ({
      id: c.external_id,
      name: c.name,
      ...(c.sku?.trim() ? { sku: c.sku.trim() } : {}),
    }));

  const prodPayload = products
    .filter((p) => !p.already_imported)
    .map((p) => mapFoodicsProductToImportPayload(p));

  const modPayload = modifiers
    .filter(
      (m): m is FoodicsModifierOptionEntity & { external_id: string } =>
        !!m.external_id && !m.already_imported
    )
    .map((m) => mapFoodicsModifierToImportPayload(m));

  const body: ImportFoodicsMenuEntitiesBody = {
    categories: catPayload,
    products: prodPayload,
    ...(modPayload.length > 0 ? { modifier_options: modPayload } : {}),
    ...(options?.withRecipe && prodPayload.length > 0
      ? { recipe_product_ids: prodPayload.map((p) => p.id) }
      : {}),
  };

  if (catPayload.length > 0 || prodPayload.length > 0 || modPayload.length > 0) {
    await importFoodicsMenuEntities(body);
  }

  return {
    imported_categories: catPayload.length,
    imported_products: prodPayload.length,
    imported_modifiers: modPayload.length,
  };
};

// React Query Hooks //

export const useFoodicsStatus = () =>
  useQuery<FoodicsIntegrationStatus, APIErrorResponse>({
    queryKey: ["foodics-status"],
    queryFn: getFoodicsStatus,
  });

export const useDisconnectFoodics = () =>
  useMutation<{ message: string }, APIErrorResponse, void>({
    mutationFn: disconnectFoodics,
  });

export const useFoodicsBranches = (enabled: boolean = true) =>
  useQuery<FoodicsBranchesResponse, APIErrorResponse>({
    queryKey: ["foodics-branches"],
    queryFn: getFoodicsBranches,
    enabled,
  });

export const useFoodicsBranchMappings = (enabled: boolean = true) =>
  useQuery<FoodicsBranchMappingsResponse, APIErrorResponse>({
    queryKey: ["foodics-branch-mappings"],
    queryFn: getFoodicsBranchMappings,
    enabled,
  });

export const useCreateFoodicsBranchMapping = () =>
  useMutation<
    { message: string; data: FoodicsBranchMapping },
    APIErrorResponse,
    CreateFoodicsBranchMappingBody
  >({
    mutationFn: createFoodicsBranchMapping,
  });

export const useDeleteFoodicsBranchMapping = () =>
  useMutation<{ message: string }, APIErrorResponse, number>({
    mutationFn: deleteFoodicsBranchMapping,
  });

export const useFoodicsMenuEntities = (
  params?: {
    products_page?: number;
    products_per_page?: number;
    categories_page?: number;
    categories_per_page?: number;
    include_inventory?: boolean | 0 | 1;
    include_product_ingredients?: boolean | 0 | 1;
    include_modifier_options?: boolean | 0 | 1;
    modifier_options_page?: number;
    modifier_options_per_page?: number;
    inventory_page?: number;
    inventory_per_page?: number;
  },
  enabled: boolean = true
) =>
  useQuery<FoodicsMenuEntitiesResponse, APIErrorResponse>({
    queryKey: ["foodics-menu-entities", params],
    queryFn: () => getFoodicsMenuEntities(params),
    enabled,
  });

export const useImportFoodicsMenuEntities = () =>
  useMutation<
    {
      message: string;
      data: {
        imported_categories: number;
        imported_products: number;
        imported_modifier_options?: number;
        imported_inventory_items?: number;
        skipped_inventory_items?: number;
        synced_production_recipes?: number;
        menu_recipes_synced?: number;
      };
    },
    APIErrorResponse,
    ImportFoodicsMenuEntitiesBody
  >({
    mutationFn: importFoodicsMenuEntities,
  });

export const useFoodicsOrdersPreview = (
  params?: {
    branch_id?: string;
    start_date?: string;
    end_date?: string;
    page?: number;
    per_page?: number;
  },
  enabled: boolean = false
) =>
  useQuery<FoodicsOrdersPreviewResponse, APIErrorResponse>({
    queryKey: ["foodics-orders-preview", params],
    queryFn: () => getFoodicsOrdersPreview(params),
    enabled,
  });

export const useImportFoodicsOrders = () =>
  useMutation<
    ImportFoodicsOrdersResponse,
    APIErrorResponse,
    ImportFoodicsOrdersBody
  >({
    mutationFn: importFoodicsOrders,
  });

export const useImportFoodicsSuppliers = () =>
  useMutation<
    ImportFoodicsSuppliersResponse,
    APIErrorResponse,
    ImportFoodicsSuppliersBody | void
  >({
    mutationFn: (data) => importFoodicsSuppliers(data ?? undefined),
  });

export const useImportAllFoodicsInventory = () =>
  useMutation<{ imported_items: number }, APIErrorResponse, void>({
    mutationFn: importAllFoodicsInventory,
  });

export const useImportAllFoodicsProduction = () =>
  useMutation<
    { imported_items: number },
    APIErrorResponse,
    { withRecipe?: boolean } | void
  >({
    mutationFn: (options) => importAllFoodicsProduction(options ?? undefined),
  });

export const useImportAllFoodicsMenu = () =>
  useMutation<
    {
      imported_categories: number;
      imported_products: number;
      imported_modifiers: number;
    },
    APIErrorResponse,
    { withRecipe?: boolean } | void
  >({
    mutationFn: (options) => importAllFoodicsMenu(options ?? undefined),
  });

export const useFoodicsSuppliers = (
  params?: { page?: number; per_page?: number },
  enabled: boolean = true
) =>
  useQuery<FoodicsSuppliersResponse, APIErrorResponse>({
    queryKey: ["foodics-suppliers", params],
    queryFn: () => getFoodicsSuppliers(params),
    enabled,
  });

